/* eslint-disable @typescript-eslint/explicit-function-return-type */

import ArticlesQuery from './gql/query/articles.cartEndpoint';
import AskChefQuery from './gql/query/askChef.cartEndpoint';
import AuthQuery from './gql/query/authorization.cartEndpoint';
import CatalogQuery from './gql/query/catalog.cartEndpoint';
import ChefBlogQuery from './gql/query/chefBlog.cartEndpoint';
import CommentsQuery from './gql/query/comments.cartEndpoint';
import CrashQuery from './gql/query/crash.cartEndpoint';
import DaData from './gql/query/dadata.cartEndpoint';
import HeaderQuery from './gql/query/header.cartEndpoint';
import MainPageQuery from './gql/query/mainPage.cartEndpoint';
import NewsQuery from './gql/query/news.cartEndpoint';
import OrderQuery from './gql/query/order.cartEndpoint';
import PersonalAccQuery from './gql/query/personalAcc.cartEndpoint';
import ProductInfoMutation from './gql/query/product.mutation.cartEndpoint';
import ProductInfoQuery from './gql/query/product.query.cartEndpoint';
import ProductList from './gql/query/productsList.cartEndpoint';
import PromotionsQuery from './gql/query/promotions.cartEndpoint';
import RecipesQuery from './gql/query/recipes.cartEndpoint';
import RedirectQuery from './gql/query/redirect.cartEndpoint';
import ReviewsQuery from './gql/query/reviews.cartEndpoint';
import SalmonQuery from './gql/query/salmonDonate.cartEndpoint';
import Subscription from './gql/query/subscription.cartEndpoint';
import TestDrivesQuery from './gql/query/testDrives.cartEndpoint';
import PromocodeQuery from './gql/query/promocode.cartEndpoint';
import TaskQuery from './gql/query/task.cartEndpoint';
import { notifications } from '@utils/notifications';
import Config from '@root/wrapperConfig';
import { CanUseWebP } from '@utils/check';
import type { DocumentNode } from 'graphql';
import { getLocalStorageItem } from '../utils/getLocalStorageItem';
import type * as CartTypes from './schema/cart';
import { cartToken } from '@store/CartStore';
import AreasQuery from './gql/query/areas.cartEndpoint';
import StoriesQuery from './gql/query/stories.cartEndpoint';
import type { GqlResponse } from '../types';
import JobQuery from './gql/query/job.cartEndpoint';
import FranchiseQuery from './gql/query/franchise.cartEndpoint';

const CanUseWebPValue = CanUseWebP(
  true // Ignore DEV mode
);

class Api {
  // Only GraphQL
  server: string;

  withCartToken: (variables: any) => any;

  constructor() {
    this.server = Config.server.graphql;
    // if (location.href.indexOf('localhost') !== -1) {
    //   this.server = 'http://192.168.100.161:9002';
    // }

    this.server += '/graphql';

    this.withCartToken = variables => ({
      ...variables,
      cartToken
    });
  }

  sendCourierLandingPageEmail = async (
    variables: CartTypes.SendCourierLandingPageEmailQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.SendCourierLandingPageEmailQuery,
      CartTypes.SendCourierLandingPageEmailQueryVariables
    >(JobQuery.sendCourierLandingPageEmail, variables);

  sendEmailForFranchise = async (
    variables: CartTypes.SendEmailForFranchiseQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.SendEmailForFranchiseQuery,
      CartTypes.SendEmailForFranchiseQueryVariables
    >(FranchiseQuery.sendEmailForFranchise, variables);

  getProductsBySearchBooster = async (
    variables: CartTypes.GetProductsBySearchBoosterQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetProductsBySearchBoosterQuery,
      CartTypes.GetProductsBySearchBoosterQueryVariables
    >(CatalogQuery.getProductsBySearchBooster, variables);

  getTopMenu = async () =>
    await this.callMethod<CartTypes.GetTopMenuQuery>(CatalogQuery.getTopMenu);

  getCatalogList = async () =>
    await this.callMethod<CartTypes.GetCatalogMainMenuQuery>(
      CatalogQuery.getCatalogList
    );

  getBannerAndText = async (
    variables: CartTypes.GetBannerAndTextQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetBannerAndTextQuery,
      CartTypes.GetBannerAndTextQueryVariables
    >(CatalogQuery.getBannerAndText, variables);

  queryCatalogFaq = async (
    variables: CartTypes.QueryCatalogFaqQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.QueryCatalogFaqQuery,
      CartTypes.QueryCatalogFaqQueryVariables
    >(CatalogQuery.queryCatalogFaq, variables);

  getPopularMainPageItems = async () =>
    await this.callMethod<CartTypes.GetPopularMainPageItemsQuery>(
      CatalogQuery.getPopularMainPageItems
    );

  getRecipesRecommend = async () =>
    await this.callMethod<CartTypes.GetRecipesRecommendQuery>(
      CatalogQuery.getRecipesRecommend
    );

  getBarMenuCategories = async (
    variables: CartTypes.GetBarMenuCategoriesQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetBarMenuCategoriesQuery,
      CartTypes.GetBarMenuCategoriesQueryVariables
    >(CatalogQuery.getBarMenuCategories, variables);

  getBarMenuTypes = async (variables?: CartTypes.BarTypesQueryVariables) =>
    await this.callMethod<
      CartTypes.BarTypesQuery,
      CartTypes.BarTypesQueryVariables
    >(CatalogQuery.getBarMenuTypes, variables);

  getMobileMenuTree = async (
    variables: CartTypes.GetMobileMenuTreeQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetMobileMenuTreeQuery,
      CartTypes.GetMobileMenuTreeQueryVariables
    >(CatalogQuery.getMobileMenuTree, variables);

  getProducts = async (variables: CartTypes.GetProductsQueryVariables) =>
    await this.callMethod<
      CartTypes.GetProductsQuery,
      CartTypes.GetProductsQueryVariables
    >(CatalogQuery.getProducts, variables);

  getBarMenuProducts = async (
    variables: CartTypes.GetBarMenuProductsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetBarMenuProductsQuery,
      CartTypes.GetBarMenuProductsQueryVariables
    >(CatalogQuery.getBarMenuProducts, variables);

  getIsProductHasAttachmentInComments = async (
    variables: CartTypes.IsProductHasAttachmentInCommentsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.IsProductHasAttachmentInCommentsQuery,
      CartTypes.IsProductHasAttachmentInCommentsQueryVariables
    >(CatalogQuery.isProductHasAttachmentInComments, variables);

  getCartShareData = async (
    variables: CartTypes.GetCartShareDataQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetCartShareDataQuery,
      CartTypes.GetCartShareDataQueryVariables
    >(CatalogQuery.getCartShareData, variables);

  getPromocodeInfo = async (
    variables: CartTypes.GetPromocodeInfoQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPromocodeInfoQuery,
      CartTypes.GetPromocodeInfoQueryVariables
    >(PromocodeQuery.getInfo, variables);

  applyPromocodeForCart = async (
    variables: CartTypes.ApplyPromocodeMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.ApplyPromocodeMutation,
      CartTypes.ApplyPromocodeMutationVariables
    >(PromocodeQuery.applyPromoCode, variables);

  sendClaim = async (variables: CartTypes.SendClaimQueryVariables) =>
    await this.callMethod<
      CartTypes.SendClaimQuery,
      CartTypes.SendClaimQueryVariables
    >(TaskQuery.sendClaim, variables);

  // Task zone
  getTaskById = async (variables: CartTypes.GetTaskByIdQueryVariables) =>
    await this.callMethod<
      CartTypes.GetTaskByIdQuery,
      CartTypes.GetTaskByIdQueryVariables
    >(TaskQuery.getTaskById, variables);

  getTaskList = async (variables: CartTypes.GetTasksQueryVariables) =>
    await this.callMethod<
      CartTypes.GetTasksQuery,
      CartTypes.GetTasksQueryVariables
    >(TaskQuery.list, variables);

  addTaskToProgress = async (
    variables: CartTypes.AddTaskToProgressMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddTaskToProgressMutation,
      CartTypes.AddTaskToProgressMutationVariables
    >(TaskQuery.toProgress, variables);

  getPeopleActiveTasksCount = async (
    variables: CartTypes.GetPeopleActiveTasksCountQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPeopleActiveTasksCountQuery,
      CartTypes.GetPeopleActiveTasksCountQueryVariables
    >(TaskQuery.getPeopleActiveTasksCount, variables);

  // Auth social zone
  // authSocialVK = variables => this.callMethod(AuthSocialQuery.vk(), variables);
  // authSocialFB = variables => this.callMethod(AuthSocialQuery.fb(), variables);

  getCharityDonations = async (
    variables: CartTypes.GetCharityDonationsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetCharityDonationsQuery,
      CartTypes.GetCharityDonationsQueryVariables
    >(SalmonQuery.getCharityDonations, variables);

  salmonDonate = async (variables: CartTypes.CharityMutationVariables) =>
    await this.callMethod<
      CartTypes.CharityMutation,
      CartTypes.CharityMutationVariables
    >(SalmonQuery.salmonDonate, variables);

  setCrash = async (variables: CartTypes.SetCrashMutationVariables) =>
    await this.callMethod<
      CartTypes.SetCrashMutation,
      CartTypes.SetCrashMutationVariables
    >(CrashQuery.setCrash, variables);

  createPayLink = async (variables: CartTypes.CreatePayLinkMutationVariables) =>
    await this.callMethod<
      CartTypes.CreatePayLinkMutation,
      CartTypes.CreatePayLinkMutationVariables
    >(OrderQuery.createPayLink, variables);

  addProductAfterPurchase = async (
    variables: CartTypes.AddProductAfterPurchaseMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddProductAfterPurchaseMutation,
      CartTypes.AddProductAfterPurchaseMutationVariables
    >(OrderQuery.addProductAfterPurchase, variables);

  checkOrderPaid = async (variables: CartTypes.CheckOrderPaidQueryVariables) =>
    await this.callMethod<
      CartTypes.CheckOrderPaidQuery,
      CartTypes.CheckOrderPaidQueryVariables
    >(OrderQuery.checkOrderPaid, variables);

  getShareCart = async (variables: CartTypes.GetShareCartQueryVariables) =>
    await this.callMethod<
      CartTypes.GetShareCartQuery,
      CartTypes.GetShareCartQueryVariables
    >(OrderQuery.getShareCart, variables);

  setOrderField = async (variables: CartTypes.SetOrderFieldMutationVariables) =>
    await this.callMethod<
      CartTypes.SetOrderFieldMutation,
      CartTypes.SetOrderFieldMutationVariables
    >(OrderQuery.setOrderField, variables);

  getSaleSectionProducts = async (
    variables: CartTypes.GetSaleSectionProductsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetSaleSectionProductsQuery,
      CartTypes.GetSaleSectionProductsQueryVariables
    >(OrderQuery.getSaleSectionProducts, variables);

  getCommentsToOrder = async (
    variables: CartTypes.GetCommentListQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetCommentListQuery,
      CartTypes.GetCommentListQueryVariables
    >(OrderQuery.getCommentsToOrder, variables);

  addDefaultComment = async (
    variables: CartTypes.AddDefaultCommentMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddDefaultCommentMutation,
      CartTypes.AddDefaultCommentMutationVariables
    >(OrderQuery.addDefaultComment, variables);

  addCartComment = async (
    variables: CartTypes.AddCartCommentMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddCartCommentMutation,
      CartTypes.AddCartCommentMutationVariables
    >(OrderQuery.addCartComment, variables);

  addCommentsToOrderOrAddress = async (
    variables: CartTypes.AddCommentsToOrderOrAddressMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddCommentsToOrderOrAddressMutation,
      CartTypes.AddCommentsToOrderOrAddressMutationVariables
    >(OrderQuery.addCommentsToOrderOrAddress, variables);

  addCommentsToAdress = async (
    variables: CartTypes.AddCommentsToAdressMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddCommentsToAdressMutation,
      CartTypes.AddCommentsToAdressMutationVariables
    >(OrderQuery.addCommentsToAdress, variables);

  addCommentsToOrder = async (
    variables: CartTypes.AddCommentsToOrderMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddCommentsToOrderMutation,
      CartTypes.AddCommentsToOrderMutationVariables
    >(OrderQuery.addCommentsToOrder, variables);

  getOrderComments = async (
    variables: CartTypes.GetOrderCommentsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetOrderCommentsQuery,
      CartTypes.GetOrderCommentsQueryVariables
    >(OrderQuery.getOrderComments, variables);

  getAddressComments = async (
    variables: CartTypes.GetAddressCommentsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetAddressCommentsQuery,
      CartTypes.GetAddressCommentsQueryVariables
    >(OrderQuery.getAddressComments, variables);

  getProductList = async (
    variables: CartTypes.GetProductsListByIdsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetProductsListByIdsQuery,
      CartTypes.GetProductsListByIdsQueryVariables
    >(ProductList.getProductsListByIds, this.withCartToken(variables));

  getRecipesMainPage = async (
    variables: CartTypes.GetRecipesMainPageQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetRecipesMainPageQuery,
      CartTypes.GetRecipesMainPageQueryVariables
    >(MainPageQuery.getRecipesMainPage, variables);

  getMainPageBanners = async () =>
    await this.callMethod<CartTypes.GetMainPageBannersQuery>(
      MainPageQuery.getMainPageBanners
    );

  getCatalogItemsByPromotionId = async () =>
    await this.callMethod<CartTypes.GetCatalogItemsByPromotionIdQuery>(
      MainPageQuery.getCatalogItemsByPromotionId
    );

  getRecommendItems = async () =>
    await this.callMethod<CartTypes.GetRecommendItemsQuery>(
      MainPageQuery.getRecommendItems
    );

  getNewbeeItems = async () =>
    await this.callMethod<CartTypes.GetNewbeeItemsQuery>(
      MainPageQuery.getNewbeeItems
    );

  addSubscriberTemp = async (
    variables: CartTypes.AddSubscriberTempMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddSubscriberTempMutation,
      CartTypes.AddSubscriberTempMutationVariables
    >(MainPageQuery.addSubscriberTemp, variables);

  callback = async (variables: CartTypes.CallbackQueryVariables) =>
    await this.callMethod<
      CartTypes.CallbackQuery,
      CartTypes.CallbackQueryVariables
    >(MainPageQuery.callback, variables);

  getStories = async () =>
    await this.callMethod<CartTypes.GetStoriesQuery>(StoriesQuery.getStories);

  getStoriesByPromotionId = async (
    variables: CartTypes.GetStoriesByPromotionIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetStoriesByPromotionIdQuery,
      CartTypes.GetStoriesByPromotionIdQueryVariables
    >(StoriesQuery.getStoriesByPromotionId, variables);

  addSlideToHistory = async (
    variables: CartTypes.AddSlideToHistoryMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddSlideToHistoryMutation,
      CartTypes.AddSlideToHistoryMutationVariables
    >(StoriesQuery.addSlideToHistory, variables);

  getIndexCoreBanners = async (
    variables: Pick<
      CartTypes.GetIndexCoreBannersQueryVariables,
      'bannersLimit' | 'storeId' | 'imageSizeFilter' | 'bannersImageSize'
    >
  ) =>
    await this.callMethod<
      CartTypes.GetIndexCoreBannersQuery,
      CartTypes.GetIndexCoreBannersQueryVariables
    >(
      MainPageQuery.getIndexCoreBanners,
      this.withCartToken({ ...variables, blocksFilter: 'banners' })
    );

  getRubricBanners = async (
    variables: CartTypes.GetRubricBannersQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetRubricBannersQuery,
      CartTypes.GetRubricBannersQueryVariables
    >(CatalogQuery.getRubricBanners, variables);

  getBannerById = async (variables: CartTypes.GetBannerByIdQueryVariables) =>
    await this.callMethod<
      CartTypes.GetBannerByIdQuery,
      CartTypes.GetBannerByIdQueryVariables
    >(MainPageQuery.getBannerById, variables);

  getIndexCoreRecipes = async (
    variables: Pick<CartTypes.GetIndexCoreRecipesQueryVariables, 'recipesLimit'>
  ) =>
    await this.callMethod<
      CartTypes.GetIndexCoreRecipesQuery,
      CartTypes.GetIndexCoreRecipesQueryVariables
    >(
      MainPageQuery.getIndexCoreRecipes,
      this.withCartToken({ ...variables, blocksFilter: 'recipes' })
    );

  getIndexCore = async (
    variables: Omit<CartTypes.GetIndexCoreQueryVariables, 'blocksFilter'>
  ) => {
    const blocksFilter: string[] = [];
    Object.keys(variables).forEach(key => {
      if (variables[key] === true) {
        blocksFilter.push(key);
      }
    });
    return await this.callMethod<
      CartTypes.GetIndexCoreQuery,
      CartTypes.GetIndexCoreQueryVariables
    >(
      MainPageQuery.getIndexCore,
      this.withCartToken({
        ...variables,
        blocksFilter
      })
    );
  };

  getRecommendedByStore = async (
    variables: CartTypes.GetRecommendedByStoreQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetRecommendedByStoreQuery,
      CartTypes.GetRecommendedByStoreQueryVariables
    >(ProductInfoQuery.getRecommendedByStore, this.withCartToken(variables));

  getIndexAddition = async (
    variables: CartTypes.GetIndexAdditionQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetIndexAdditionQuery,
      CartTypes.GetIndexAdditionQueryVariables
    >(MainPageQuery.getIndexAddition, this.withCartToken(variables));

  getTopNewestReviews = async (
    variables: CartTypes.GetTopNewestReviewsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetTopNewestReviewsQuery,
      CartTypes.GetTopNewestReviewsQueryVariables
    >(ReviewsQuery.getTopNewestReviews, variables);

  getIndexReviews = async (
    variables: CartTypes.GetIndexReviewsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetIndexReviewsQuery,
      CartTypes.GetIndexReviewsQueryVariables
    >(MainPageQuery.getIndexReviews, variables);

  sendEmailForTableBooking = async (
    variables: CartTypes.SendEmailForTableBookingQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.SendEmailForTableBookingQuery,
      CartTypes.SendEmailForTableBookingQueryVariables
    >(MainPageQuery.sendEmailForTableBooking, variables);

  sendEmailForCateringBooking = async (
    variables: CartTypes.SendEmailForCateringQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.SendEmailForCateringQuery,
      CartTypes.SendEmailForCateringQueryVariables
    >(MainPageQuery.sendEmailForCatering, variables);

  getTestDrives = async (variables: CartTypes.GetTestDrivesQueryVariables) =>
    await this.callMethod<
      CartTypes.GetTestDrivesQuery,
      CartTypes.GetTestDrivesQueryVariables
    >(TestDrivesQuery.getTestDrives, variables);

  getTestDriveById = async (
    variables: CartTypes.GetTestDriveByIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetTestDriveByIdQuery,
      CartTypes.GetTestDriveByIdQueryVariables
    >(TestDrivesQuery.getTestDriveById, variables);

  getTestDrivesCount = async (
    variables: CartTypes.GetTestDrivesCountQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetTestDrivesCountQuery,
      CartTypes.GetTestDrivesCountQueryVariables
    >(TestDrivesQuery.getTestDrivesCount, variables);

  getAllReceivedTestDriveProducts = async () =>
    await this.callMethod<CartTypes.GetAllReceivedTestDriveProductsQuery>(
      TestDrivesQuery.getAllReceivedTestDriveProducts
    );

  getReviews = async (variables: CartTypes.GetReviewsAllQueryVariables) =>
    await this.callMethod<
      CartTypes.GetReviewsAllQuery,
      CartTypes.GetReviewsAllQueryVariables
    >(ReviewsQuery.getReviews, variables);

  getAverageReviewsRating = async (
    variables: CartTypes.GetAverageReviewsRatingQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetAverageReviewsRatingQuery,
      CartTypes.GetAverageReviewsRatingQueryVariables
    >(ReviewsQuery.getAverageReviewsRating, variables);

  getReviewsRating = async (
    variables: CartTypes.GetReviewsRatingQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetReviewsRatingQuery,
      CartTypes.GetReviewsRatingQueryVariables
    >(ReviewsQuery.getReviewsRating, variables);

  addReview = async (variables: CartTypes.AddReviewMutationVariables) =>
    await this.callMethod<
      CartTypes.AddReviewMutation,
      CartTypes.AddReviewMutationVariables
    >(ReviewsQuery.addReview, variables);

  getReviewTagList = async (
    variables: CartTypes.GetReviewTagListQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetReviewTagListQuery,
      CartTypes.GetReviewTagListQueryVariables
    >(ReviewsQuery.getReviewTagList, variables);

  voteForReview = async (variables: CartTypes.VoteForReviewMutationVariables) =>
    await this.callMethod<
      CartTypes.VoteForReviewMutation,
      CartTypes.VoteForReviewMutationVariables
    >(ReviewsQuery.voteForReview, variables);

  voteForComment = async (
    variables: CartTypes.VoteForCommentMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.VoteForCommentMutation,
      CartTypes.VoteForCommentMutationVariables
    >(CommentsQuery.voteForComment, variables);

  rateAnswer = async (variables: CartTypes.RateAnswerMutationVariables) =>
    await this.callMethod<
      CartTypes.RateAnswerMutation,
      CartTypes.RateAnswerMutationVariables
    >(ReviewsQuery.rateAnswer, variables);

  getProductInfoTemp = async (
    variables: CartTypes.GetProductInfoTempQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetProductInfoTempQuery,
      CartTypes.GetProductInfoTempQueryVariables
    >(ProductInfoQuery.getProductInfoTemp, variables);

  getProductAlsoboughtAndRecommend = async (
    variables: CartTypes.GetProductAlsoboughtAndRecommendQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetProductAlsoboughtAndRecommendQuery,
      CartTypes.GetProductAlsoboughtAndRecommendQueryVariables
    >(
      ProductInfoQuery.getProductAlsoboughtAndRecommend,
      this.withCartToken(variables)
    );

  getCatalogAnalogs = async (
    variables: CartTypes.GetCatalogAnalogsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetCatalogAnalogsQuery,
      CartTypes.GetCatalogAnalogsQueryVariables
    >(ProductInfoQuery.getCatalogAnalogs, variables);

  getProductRecipes = async (
    variables: CartTypes.GetProductRecipesByIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetProductRecipesByIdQuery,
      CartTypes.GetProductRecipesByIdQueryVariables
    >(ProductInfoQuery.getProductRecipes, variables);

  getPromoRecipesByProductId = async (
    variables: CartTypes.GetPromoRecipesByProductIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPromoRecipesByProductIdQuery,
      CartTypes.GetPromoRecipesByProductIdQueryVariables
    >(ProductInfoQuery.getPromoRecipesByProductId, variables);

  getProductQuickInfo = async (
    variables: CartTypes.GetProductQuickInfoQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetProductQuickInfoQuery,
      CartTypes.GetProductQuickInfoQueryVariables
    >(ProductInfoQuery.getProductQuickInfo, variables);

  getRecommendationsToCart = async (
    variables: CartTypes.GetRecommendationsToCartQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetRecommendationsToCartQuery,
      CartTypes.GetRecommendationsToCartQueryVariables
    >(ProductInfoQuery.getRecommendationsToCart, this.withCartToken(variables));

  getRecommendationsToOrder = async (
    variables: CartTypes.GetRecommendationsToOrderQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetRecommendationsToOrderQuery,
      CartTypes.GetRecommendationsToOrderQueryVariables
    >(
      ProductInfoQuery.getRecommendationsToOrder,
      this.withCartToken(variables)
    );

  actionFavoriteItem = async (
    variables: CartTypes.ActionFavoriteItemMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.ActionFavoriteItemMutation,
      CartTypes.ActionFavoriteItemMutationVariables
    >(ProductInfoMutation.actionFavoriteItem, variables);

  getRefundInfo = async (variables: CartTypes.GetRefundInfoQueryVariables) =>
    await this.callMethod<
      CartTypes.GetRefundInfoQuery,
      CartTypes.GetRefundInfoQueryVariables
    >(ProductInfoQuery.getRefundInfo, variables);

  applyForRefund = async (
    variables: CartTypes.ApplyForRefundMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.ApplyForRefundMutation,
      CartTypes.ApplyForRefundMutationVariables
    >(ProductInfoMutation.applyForRefund, variables);

  getProductsBySupplierId = async (
    variables: CartTypes.GetProductsBySupplierIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetProductsBySupplierIdQuery,
      CartTypes.GetProductsBySupplierIdQueryVariables
    >(ProductInfoQuery.getProductsBySupplierId, variables);

  getAllNews = async (variables: CartTypes.FindNewsAllQueryVariables) =>
    await this.callMethod<
      CartTypes.FindNewsAllQuery,
      CartTypes.FindNewsAllQueryVariables
    >(NewsQuery.getAllNews, variables);

  getSingleNews = async (variables: CartTypes.FindNewsByIdQueryVariables) =>
    await this.callMethod<
      CartTypes.FindNewsByIdQuery,
      CartTypes.FindNewsByIdQueryVariables
    >(NewsQuery.getSingleNews, variables);

  getCommentsNews = async (
    variables: CartTypes.FindNewsCommentsByIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.FindNewsCommentsByIdQuery,
      CartTypes.FindNewsCommentsByIdQueryVariables
    >(NewsQuery.getCommentsNews, variables);

  getCountNewsItems = async () =>
    await this.callMethod<CartTypes.CountNewsItemsQuery>(
      NewsQuery.getCountNewsItems
    );

  getPromotions = async (
    variables: CartTypes.FindPromotionsAllQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.FindPromotionsAllQuery,
      CartTypes.FindPromotionsAllQueryVariables
    >(PromotionsQuery.getList, variables);

  getCountPromotions = async () =>
    await this.callMethod<CartTypes.CountPromotionsItemsQuery>(
      PromotionsQuery.getCount
    );

  getPromotionById = async (
    variables: CartTypes.FindPromotionByIdTempQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.FindPromotionByIdTempQuery,
      CartTypes.FindPromotionByIdTempQueryVariables
    >(PromotionsQuery.getPromotion, variables);

  getPeopleBillAmount = async () =>
    await this.callMethod<CartTypes.GetPeopleBillAmountQuery>(
      PersonalAccQuery.getPeopleBillAmount
    );

  getPeopleBillLog = async (
    variables: CartTypes.GetPeopleBillLogQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPeopleBillLogQuery,
      CartTypes.GetPeopleBillLogQueryVariables
    >(PersonalAccQuery.getPeopleBillLog, variables);

  getTestDriveProductsStats = async (
    variables: CartTypes.GetTestDriveProductsStatsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetTestDriveProductsStatsQuery,
      CartTypes.GetTestDriveProductsStatsQueryVariables
    >(PersonalAccQuery.getTestDriveProductsStats, variables);

  getOrderHistoryProductStats = async (
    variables: CartTypes.OrderHistoryProductStatsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.OrderHistoryProductStatsQuery,
      CartTypes.OrderHistoryProductStatsQueryVariables
    >(PersonalAccQuery.getOrderHistoryProductStats, variables);

  getPeopleSettings = async () =>
    await this.callMethod<CartTypes.GetPeopleSettingsQuery>(
      PersonalAccQuery.getPeopleSettings
    );

  oneSignalSpamCheck = async () =>
    await this.callMethod<CartTypes.OneSignalSpamCheckQuery>(
      PersonalAccQuery.oneSignalSpamCheck
    );

  oneSignalSpamToogle = async (
    variables: CartTypes.OneSignalSpamToogleMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.OneSignalSpamToogleMutation,
      CartTypes.OneSignalSpamToogleMutationVariables
    >(PersonalAccQuery.oneSignalSpamToogle, variables);

  savePeopleSettings = async (
    variables: CartTypes.SavePeopleSettingsMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.SavePeopleSettingsMutation,
      CartTypes.SavePeopleSettingsMutationVariables
    >(PersonalAccQuery.savePeopleSettings, variables);

  getPeopleReviews = async (
    variables: CartTypes.GetPeopleReviewsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPeopleReviewsQuery,
      CartTypes.GetPeopleReviewsQueryVariables
    >(PersonalAccQuery.getPeopleReviews, variables);

  editPeopleReviewById = async (
    variables: CartTypes.EditPeopleReviewByIdMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.EditPeopleReviewByIdMutation,
      CartTypes.EditPeopleReviewByIdMutationVariables
    >(PersonalAccQuery.editPeopleReviewById, variables);

  getProductSubs = async (variables: CartTypes.GetProductSubsQueryVariables) =>
    await this.callMethod<
      CartTypes.GetProductSubsQuery,
      CartTypes.GetProductSubsQueryVariables
    >(PersonalAccQuery.getProductSubs, variables);

  updateCatalogSubs = async (
    variables: CartTypes.UpdateCatalogSubsMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.UpdateCatalogSubsMutation,
      CartTypes.UpdateCatalogSubsMutationVariables
    >(PersonalAccQuery.updateCatalogSubs, variables);

  clearCatalogSubs = async (
    variables: CartTypes.ClearCatalogSubsMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.ClearCatalogSubsMutation,
      CartTypes.ClearCatalogSubsMutationVariables
    >(PersonalAccQuery.clearCatalogSubs, variables);

  GRSubAction = async (variables: CartTypes.GrSubActionMutationVariables) =>
    await this.callMethod<
      CartTypes.GrSubActionMutation,
      CartTypes.GrSubActionMutationVariables
    >(PersonalAccQuery.GRSubAction, variables);

  checkGRSub = async () =>
    await this.callMethod<CartTypes.CheckGrSubMutation>(
      PersonalAccQuery.checkGRSub
    );

  deleteSubscriptionGR = async () =>
    await this.callMethod<CartTypes.DeleteSubscribtionMutation>(
      PersonalAccQuery.deleteSubscriptionGR
    );

  subscribeSMS = async (variables: CartTypes.SubscribeSmsMutationVariables) =>
    await this.callMethod<
      CartTypes.SubscribeSmsMutation,
      CartTypes.SubscribeSmsMutationVariables
    >(PersonalAccQuery.subscribeSMS, variables);

  getPeopleRecipes = async (
    variables: CartTypes.GetPeopleRecipesQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPeopleRecipesQuery,
      CartTypes.GetPeopleRecipesQueryVariables
    >(PersonalAccQuery.getPeopleRecipes, variables);

  getPeopleProducts = async (
    variables: CartTypes.GetPeopleProductsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPeopleProductsQuery,
      CartTypes.GetPeopleProductsQueryVariables
    >(PersonalAccQuery.getPeopleProducts, variables);

  getPeopleProfile = async () =>
    await this.callMethod<CartTypes.GetPeopleProfileQuery>(
      PersonalAccQuery.getPeopleProfile
    );

  savePeopleProfile = async (
    variables: CartTypes.SavePeopleProfileMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.SavePeopleProfileMutation,
      CartTypes.SavePeopleProfileMutationVariables
    >(PersonalAccQuery.savePeopleProfile, variables);

  getCartTemplatesList = async (
    variables: CartTypes.GetCartTemplatesListQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetCartTemplatesListQuery,
      CartTypes.GetCartTemplatesListQueryVariables
    >(PersonalAccQuery.getCartTemplatesList, variables);

  deleteCartTemplateItem = async (
    variables: CartTypes.DeleteCartTemplateItemQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.DeleteCartTemplateItemQuery,
      CartTypes.DeleteCartTemplateItemQueryVariables
    >(PersonalAccQuery.deleteCartTemplateItem, variables);

  getPeopleOrders = async (
    variables: CartTypes.GetPeopleOrdersQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPeopleOrdersQuery,
      CartTypes.GetPeopleOrdersQueryVariables
    >(PersonalAccQuery.getPeopleOrders, variables);

  getPeopleOrderById = async (
    variables: CartTypes.GetPeopleOrderByIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPeopleOrderByIdQuery,
      CartTypes.GetPeopleOrderByIdQueryVariables
    >(PersonalAccQuery.getPeopleOrderById, variables);

  saveCartTemplateItem = async (
    variables: CartTypes.SaveCartTemplateItemMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.SaveCartTemplateItemMutation,
      CartTypes.SaveCartTemplateItemMutationVariables
    >(PersonalAccQuery.saveCartTemplateItem, variables);

  confirmOrderById = async (
    variables: CartTypes.ConfirmOrderByIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.ConfirmOrderByIdQuery,
      CartTypes.ConfirmOrderByIdQueryVariables
    >(PersonalAccQuery.confirmOrderById, variables);

  getOrderAndAddressComments = async (
    variables: CartTypes.GetOrderAndAddressCommentsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetOrderAndAddressCommentsQuery,
      CartTypes.GetOrderAndAddressCommentsQueryVariables
    >(PersonalAccQuery.getOrderAndAddressComments, variables);

  getThankYouPromotionProducts = async (
    variables: CartTypes.GetThankYouPromotionProductsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetThankYouPromotionProductsQuery,
      CartTypes.GetThankYouPromotionProductsQueryVariables
    >(PersonalAccQuery.getThankYouPromotionProducts, variables);

  getProductsByIds = async (
    variables: CartTypes.GetProductsByIdsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetProductsByIdsQuery,
      CartTypes.GetProductsByIdsQueryVariables
    >(PersonalAccQuery.getProductsByIds, variables);

  rebindCartPid = async (variables: CartTypes.RebindCartPidMutationVariables) =>
    await this.callMethod<
      CartTypes.RebindCartPidMutation,
      CartTypes.RebindCartPidMutationVariables
    >(PersonalAccQuery.rebindCartPid, variables);

  addTipsForCourier = async (
    variables: CartTypes.AddTipsForCourierMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddTipsForCourierMutation,
      CartTypes.AddTipsForCourierMutationVariables
    >(PersonalAccQuery.addTipsForCourier, variables);

  getOrdersThatUserCanChange = async () =>
    await this.callMethod<CartTypes.GetOrdersThatUserCanChangeQuery>(
      PersonalAccQuery.getOrdersThatUserCanChange
    );

  getPeopleRecycleStats = async (
    variables: CartTypes.GetPeopleRecycleStatsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPeopleRecycleStatsQuery,
      CartTypes.GetPeopleRecycleStatsQueryVariables
    >(PersonalAccQuery.getPeopleRecycleStats, variables);

  getNewsWithPeopleCharityDonations = async (
    variables: CartTypes.GetNewsWithPeopleCharityDonationsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetNewsWithPeopleCharityDonationsQuery,
      CartTypes.GetNewsWithPeopleCharityDonationsQueryVariables
    >(PersonalAccQuery.getNewsWithPeopleCharityDonations, variables);

  setOrderPaymentType = async (
    variables: CartTypes.SetOrderPaymentTypeMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.SetOrderPaymentTypeMutation,
      CartTypes.SetOrderPaymentTypeMutationVariables
    >(PersonalAccQuery.setOrderPaymentType, variables);

  getPeopleLastDeliveredOrderId = async () =>
    await this.callMethod<CartTypes.GetPeopleLastDeliveredOrderIdQuery>(
      PersonalAccQuery.getPeopleLastDeliveredOrderId
    );

  getSBPLink = async (variables: CartTypes.GetSbpLinkQueryVariables) =>
    await this.callMethod<
      CartTypes.GetSbpLinkQuery,
      CartTypes.GetSbpLinkQueryVariables
    >(PersonalAccQuery.getSBPLink, variables);

  checkSBPStatus = async (
    variables: CartTypes.CheckSbpStatusesQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.CheckSbpStatusesQuery,
      CartTypes.CheckSbpStatusesQueryVariables
    >(PersonalAccQuery.checkSBPStatus, variables);

  getTotalFavorites = async () =>
    await this.callMethod<CartTypes.GetPeopleFavoritesProductsQuery>(
      HeaderQuery.getTotalFavorites
    );

  getSearchExample = async () =>
    await this.callMethod<CartTypes.GetSearchExampleQuery>(
      HeaderQuery.getSearchExample
    );

  getDefaultNearestDelivery = async () =>
    await this.callMethod<CartTypes.GetDefaultNearestDeliveryQuery>(
      HeaderQuery.getDefaultNearestDelivery
    );

  getCatalogMainMenu = async () =>
    await this.callMethod<CartTypes.GetCatalogMainMenuQuery>(
      HeaderQuery.getCatalogMainMenu
    );

  updatePeopleStoreId = async () =>
    await this.callMethod<CartTypes.UpdatePeopleStoreIdQuery>(
      HeaderQuery.updatePeopleStoreId
    );

  // getSearchQueries = async () =>
  //   await this.callMethod<CartTypes.GetSearchQueriesQuery>(
  //     HeaderQuery.getSearchQueries
  //   );

  getPeopleSearchQueries = async () =>
    await this.callMethod<CartTypes.GetPeopleSearchQueriesQuery>(
      HeaderQuery.getPeopleSearchQueries
    );

  getPopularQueriesAndCategories = async () =>
    await this.callMethod<CartTypes.GetPopularQueriesAndCategoriesQuery>(
      HeaderQuery.getPopularQueriesAndCategories
    );

  deletePeopleSearchQuery = async (
    variables: CartTypes.DeletePeopleSearchQueryMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.DeletePeopleSearchQueryMutation,
      CartTypes.DeletePeopleSearchQueryMutationVariables
    >(HeaderQuery.deletePeopleSearchQuery, variables);

  getDeliveryCloseInfo = async () =>
    await this.callMethod<CartTypes.GetDeliveryCloseInfoQuery>(
      HeaderQuery.getDeliveryCloseInfo
    );

  getRedirects = async () =>
    await this.callMethod<CartTypes.GetRedirectsActiveQuery>(
      RedirectQuery.getRedirectsActive
    );

  // RECIPES
  getRecipesWithFilters = async (
    variables: CartTypes.GetRecipesWithFiltersQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetRecipesWithFiltersQuery,
      CartTypes.GetRecipesWithFiltersQueryVariables
    >(RecipesQuery.getRecipesWithFilters, variables);

  getAuthorRecipes = async (
    variables: CartTypes.GetAuthorRecipesQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetAuthorRecipesQuery,
      CartTypes.GetAuthorRecipesQueryVariables
    >(RecipesQuery.getAuthorRecipes, variables);

  getRecipesCategories = async (
    variables: CartTypes.GetRecipesCategoriesQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetRecipesCategoriesQuery,
      CartTypes.GetRecipesCategoriesQueryVariables
    >(RecipesQuery.getRecipesCategories, variables);

  getRecipesPopular = async (
    variables?: CartTypes.GetRecipesPopularQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetRecipesPopularQuery,
      CartTypes.GetRecipesPopularQueryVariables
    >(RecipesQuery.getRecipesPopular, variables);

  getRecipeById = async (variables: CartTypes.GetRecipeByIdQueryVariables) =>
    await this.callMethod<
      CartTypes.GetRecipeByIdQuery,
      CartTypes.GetRecipeByIdQueryVariables
    >(RecipesQuery.getRecipeById, variables);

  getRecipeCommentsByRecipeId = async (
    variables: CartTypes.GetRecipeCommentsByRecipeIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetRecipeCommentsByRecipeIdQuery,
      CartTypes.GetRecipeCommentsByRecipeIdQueryVariables
    >(RecipesQuery.getRecipeCommentsByRecipeId, variables);

  getRecipesFilters = async () =>
    await this.callMethod<CartTypes.GetRecipesFiltersQuery>(
      RecipesQuery.getRecipesFilters
    );

  // addRecipesItem = variables => this.callMethod(RecipesQuery.addRecipesItemgetRecipesWithFilters,variables)
  addRecipesItem = async (
    variables: CartTypes.AddRecipesItemMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddRecipesItemMutation,
      CartTypes.AddRecipesItemMutationVariables
    >(RecipesQuery.addRecipesItem, variables);

  voteForRecipe = async (variables: CartTypes.VoteForRecipeMutationVariables) =>
    await this.callMethod<
      CartTypes.VoteForRecipeMutation,
      CartTypes.VoteForRecipeMutationVariables
    >(RecipesQuery.voteForRecipe, variables);

  voteForArticle = async (
    variables: CartTypes.VoteForArticleMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.VoteForArticleMutation,
      CartTypes.VoteForArticleMutationVariables
    >(RecipesQuery.voteForArticle, variables);

  getAuthor = async (variables: CartTypes.GetAuthorQueryVariables) =>
    await this.callMethod<
      CartTypes.GetAuthorQuery,
      CartTypes.GetAuthorQueryVariables
    >(RecipesQuery.getAuthor, variables);

  // ARTICLES
  getArticles = async (variables: CartTypes.GetArticlesQueryVariables) =>
    await this.callMethod<
      CartTypes.GetArticlesQuery,
      CartTypes.GetArticlesQueryVariables
    >(ArticlesQuery.getArticles, variables);

  getLastArticlesData = async (
    variables: CartTypes.GetLastArticlesDataQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetLastArticlesDataQuery,
      CartTypes.GetLastArticlesDataQueryVariables
    >(ArticlesQuery.getLastArticlesData, variables);

  getArticleById = async (variables: CartTypes.GetArticleByIdQueryVariables) =>
    await this.callMethod<
      CartTypes.GetArticleByIdQuery,
      CartTypes.GetArticleByIdQueryVariables
    >(ArticlesQuery.getArticleById, variables);

  getArticlesCommentsByArticleId = async (
    variables: CartTypes.GetArticlesCommentsByArticleIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetArticlesCommentsByArticleIdQuery,
      CartTypes.GetArticlesCommentsByArticleIdQueryVariables
    >(ArticlesQuery.getArticlesCommentsByArticleId, variables);

  getArticleRelatedCatalogItems = async (
    variables: CartTypes.GetArticleRelatedCatalogItemsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetArticleRelatedCatalogItemsQuery,
      CartTypes.GetArticleRelatedCatalogItemsQueryVariables
    >(ArticlesQuery.getArticleRelatedCatalogItems, variables);

  getArticleRelatedArticles = async (
    variables: CartTypes.GetArticleRelatedArticlesQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetArticleRelatedArticlesQuery,
      CartTypes.GetArticleRelatedArticlesQueryVariables
    >(ArticlesQuery.getArticleRelatedArticles, variables);

  getArticleRelatedRecipes = async (
    variables: CartTypes.GetArticleRelatedRecipesQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetArticleRelatedRecipesQuery,
      CartTypes.GetArticleRelatedRecipesQueryVariables
    >(ArticlesQuery.getArticleRelatedRecipes, variables);

  getBlogItemById = async (
    variables: CartTypes.GetBlogItemByIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetBlogItemByIdQuery,
      CartTypes.GetBlogItemByIdQueryVariables
    >(ArticlesQuery.getBlogItemById, variables);

  authInfo = async () =>
    await this.callMethod<CartTypes.MeQuery>(AuthQuery.authInfo);

  loginOrRegisterBySms = async (
    variables: CartTypes.LoginOrRegisterBySmsMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.LoginOrRegisterBySmsMutation,
      CartTypes.LoginOrRegisterBySmsMutationVariables
    >(AuthQuery.loginOrRegisterBySms, variables);

  changeEmailBySms = async (
    variables: CartTypes.ChangeEmailBySmsMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.ChangeEmailBySmsMutation,
      CartTypes.ChangeEmailBySmsMutationVariables
    >(PersonalAccQuery.changeEmailBySms, variables);

  partnerLoginOrRegister = async (
    variables: CartTypes.PartnerLoginOrRegisterMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.PartnerLoginOrRegisterMutation,
      CartTypes.PartnerLoginOrRegisterMutationVariables
    >(AuthQuery.partnerLoginOrRegister, variables);

  loginOrRegisterByEmail = async (
    variables: CartTypes.LoginOrRegisterByEmailMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.LoginOrRegisterByEmailMutation,
      CartTypes.LoginOrRegisterByEmailMutationVariables
    >(AuthQuery.loginOrRegisterByEmail, variables);

  checkPeopleExist = async (
    variables: CartTypes.CheckPeopleExistQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.CheckPeopleExistQuery,
      CartTypes.CheckPeopleExistQueryVariables
    >(AuthQuery.checkPeopleExist, variables);

  loginByPhoneAnyWay = async (
    variables: CartTypes.LoginByPhoneAnyWayMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.LoginByPhoneAnyWayMutation,
      CartTypes.LoginByPhoneAnyWayMutationVariables
    >(AuthQuery.loginByPhoneAnyWay, variables);

  maxLoginStart = async () =>
    await this.callMethod<CartTypes.MaxLoginStartMutation>(
      AuthQuery.maxLoginStart
    );

  maxLoginCheck = async (
    variables: CartTypes.MaxLoginCheckMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.MaxLoginCheckMutation,
      CartTypes.MaxLoginCheckMutationVariables
    >(AuthQuery.maxLoginCheck, variables);

  authorization = async (variables: CartTypes.LoginMutationVariables) =>
    await this.callMethod<
      CartTypes.LoginMutation,
      CartTypes.LoginMutationVariables
    >(AuthQuery.authorization, variables);

  registration = async variables =>
    await this.callMethod(AuthQuery.registration, variables);

  resetPass = async (variables: CartTypes.ForgotPasswordMutationVariables) =>
    await this.callMethod<
      CartTypes.ForgotPasswordMutation,
      CartTypes.ForgotPasswordMutationVariables
    >(AuthQuery.resetPass, variables);

  logout = async () =>
    await this.callMethod<CartTypes.LogoutMutation>(AuthQuery.logout);

  getFaqsAll = async (variables: CartTypes.GetFaqsAllQueryVariables) =>
    await this.callMethod<
      CartTypes.GetFaqsAllQuery,
      CartTypes.GetFaqsAllQueryVariables
    >(AskChefQuery.getFaqsAll, variables);

  addFaqItem = async (variables: CartTypes.AddFaqItemMutationVariables) =>
    await this.callMethod<
      CartTypes.AddFaqItemMutation,
      CartTypes.AddFaqItemMutationVariables
    >(AskChefQuery.addFaqItem, variables);

  getChefInfo = async (variables: CartTypes.GetBlogItemsQueryVariables) =>
    await this.callMethod<
      CartTypes.GetBlogItemsQuery,
      CartTypes.GetBlogItemsQueryVariables
    >(ChefBlogQuery.getChefInfo, variables);

  getRecipesVideoCodes = async () =>
    await this.callMethod<CartTypes.GetRecipesVideoCodesQuery>(
      ChefBlogQuery.getRecipesVideoCodes
    );

  getPopularArticles = async (
    variables: CartTypes.GetPopularArticlesQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPopularArticlesQuery,
      CartTypes.GetPopularArticlesQueryVariables
    >(ChefBlogQuery.getPopularArticles, variables);

  getPopularRecipes = async (
    variables: CartTypes.GetPopularRecipesQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetPopularRecipesQuery,
      CartTypes.GetPopularRecipesQueryVariables
    >(ChefBlogQuery.getPopularRecipes, variables);

  addComment = async (variables: CartTypes.AddCommentMutationVariables) =>
    await this.callMethod<
      CartTypes.AddCommentMutation,
      CartTypes.AddCommentMutationVariables
    >(CommentsQuery.addComment, variables);

  getCommentsProduct = async (
    variables: CartTypes.GetProductCommentsByIdQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.GetProductCommentsByIdQuery,
      CartTypes.GetProductCommentsByIdQueryVariables
    >(CommentsQuery.getCommentsProduct, variables);

  peopleCommentsStats = async (
    variables: CartTypes.PeopleCommentsStatsQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.PeopleCommentsStatsQuery,
      CartTypes.PeopleCommentsStatsQueryVariables
    >(CommentsQuery.peopleCommentsStats, variables);

  addDadata = async (variables: CartTypes.AddDadataQueryVariables) =>
    await this.callMethod<
      CartTypes.AddDadataQuery,
      CartTypes.AddDadataQueryVariables
    >(DaData.addDadata, variables);

  productSubscribe = async (
    variables: CartTypes.ProductSubscribeMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.ProductSubscribeMutation,
      CartTypes.ProductSubscribeMutationVariables
    >(Subscription.productSubscribe, variables);

  checkPeopleSubscription = async (
    variables: CartTypes.CheckPeopleSubscriptionQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.CheckPeopleSubscriptionQuery,
      CartTypes.CheckPeopleSubscriptionQueryVariables
    >(Subscription.checkPeopleSubscription, variables);

  confirmSubscribe = async (
    variables: CartTypes.ConfirmSubscribeQueryVariables
  ) =>
    await this.callMethod<
      CartTypes.ConfirmSubscribeQuery,
      CartTypes.ConfirmSubscribeQueryVariables
    >(Subscription.confirmSubscribe, variables);

  addRecipeToFavoritesList = async (
    variables: CartTypes.AddRecipeToFavoritesListMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.AddRecipeToFavoritesListMutation,
      CartTypes.AddRecipeToFavoritesListMutationVariables
    >(RecipesQuery.addRecipeToFavoritesList, variables);

  removeRecipeFromFavoritesList = async (
    variables: CartTypes.RemoveRecipeFromFavoritesListMutationVariables
  ) =>
    await this.callMethod<
      CartTypes.RemoveRecipeFromFavoritesListMutation,
      CartTypes.RemoveRecipeFromFavoritesListMutationVariables
    >(RecipesQuery.removeRecipeFromFavoritesList, variables);

  getOrganizationRating = async () =>
    await this.callMethod<CartTypes.GetOrganizationRatingQuery>(
      PersonalAccQuery.getOrganizationRating
    );

  getRanges = async () =>
    await this.callMethod<
      CartTypes.GetRangesQuery,
      CartTypes.GetRangesQueryVariables
    >(AreasQuery.getRanges);

  getRangeById = async (variables: CartTypes.GetRangeByIdQueryVariables) =>
    await this.callMethod<
      CartTypes.GetRangeByIdQuery,
      CartTypes.GetRangeByIdQueryVariables
    >(AreasQuery.getRangeById, variables);

  // updateCartItemsTest = variables => this.callMethod(TestsQuery.updateCartItemsTest,variables);
  async callMethod<T, K = void>(
    query: DocumentNode,
    variables?: K
    // data = {},
    // method = 'POST',
    // headers = {}
  ): Promise<T> {
    const headerToken = getLocalStorageItem<string>('headerToken');
    const cartToken = getLocalStorageItem<string>('cartToken');
    const xStoreId = getLocalStorageItem<string>('x-store-id');

    return await new Promise((resolve, reject) => {
      fetch(this.server, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-People-Token': headerToken,
          'X-Cart-Token': cartToken,
          'x-webp-token': CanUseWebPValue ? '1' : '0',
          'x-store-id': xStoreId,
          'x-nearest-first-sorting': 'true',
          'x-request-id': `${Date.now()}::${Math.trunc(Math.random() * 10000)}`,
          credentials: 'include'
          // ...headers
        },
        body: JSON.stringify({
          query: query.loc?.source.body,
          variables
          // ...data
        })
      })
        .then(async response => await response.json())
        .then(async (data: GqlResponse<T>) => {
          let message = '';

          if (typeof data.data !== 'undefined') {
            for (const prop in data.data) {
              const methodProp = data.data[prop];

              if (
                methodProp &&
                typeof methodProp.warning !== 'undefined' &&
                methodProp.warning != null &&
                methodProp.warning.length !== 0
              ) {
                message = data.data[prop].warning;
              }

              if (
                methodProp &&
                typeof methodProp.error !== 'undefined' &&
                methodProp.error != null &&
                methodProp.error.length !== 0
              ) {
                message = data.data[prop].error;
              }
            }

            if (message.length !== 0) {
              notifications(message, 'Внимание');
            }
          }

          if (
            typeof data.data === 'undefined' ||
            data.data === null ||
            typeof data.errors !== 'undefined'
          ) {
            return await Promise.reject(data.errors);
          }
          resolve(data.data);
        })
        .catch(e => {
          notifications(e);
          reject(e);
        });
    });
  }
}

type ApiInstance = InstanceType<typeof Api>;
export type ApiMethodKeys = {
  [K in keyof ApiInstance]: ApiInstance[K] extends (...args: any) => any
    ? K
    : never;
}[keyof ApiInstance];
export type ApiT = Pick<ApiInstance, ApiMethodKeys>;

export default new Api();
